/**
 * Camada das marcações trazidas da análise preliminar (E47.F3, ADR-0058 §2.5.bis). // ux-neutrality:allow doc-comment
 * // ux-neutrality:allow doc-comment
 * Ponto de partida do perito: as marcações da triagem chegam à mesa desenhadas // ux-neutrality:allow doc-comment
 * TRACEJADAS, distintas das anotações dele (sólidas). Ele assume cada uma como // ux-neutrality:allow doc-comment
 * sua (adotar) ou descarta. // ux-neutrality:allow doc-comment
 * // ux-neutrality:allow doc-comment
 * Por que uma camada SEPARADA do AnnotationLayer, e não um estilo dentro dele: // ux-neutrality:allow doc-comment
 * a captura para o laudo (E36.F4) rasteriza os SVGs do viewport, e este SVG // ux-neutrality:allow doc-comment
 * carrega `capture-exclude` — é o que mantém material da raia isolada FORA do // ux-neutrality:allow doc-comment
 * documento pericial (ADR-0056 §2.2). Adotar move a marcação para o // ux-neutrality:allow doc-comment
 * AnnotationLayer (a camada capturável), então só entra no laudo o que o humano // ux-neutrality:allow doc-comment
 * adotou. Misturar as duas num SVG só tornaria a exclusão impossível. // ux-neutrality:allow doc-comment
 * // ux-neutrality:allow doc-comment
 * Read-only quanto à geometria: não se move nem se redimensiona uma sugestão — // ux-neutrality:allow doc-comment
 * assuma-a primeiro; aí ela é sua e o AnnotationLayer a torna editável. // ux-neutrality:allow doc-comment
 */
import { useEffect, useRef, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import {
  annotationStrokePx,
  arrowHeadLenPx,
  arrowHeadPoints,
  type ImageSize,
} from '@/lib/annotation_geometry';
import {
  ANNOTATION_COLOR_HEX,
  isArrowGeometry,
  type MesaAnnotationData,
} from '@/lib/mesa_annotations';

export interface TriagemAnnotationLayerProps {
  /** Só as marcações com `origem === 'triagem'` — o caller filtra. */
  annotations: MesaAnnotationData[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /**
   * Ferramenta ativa (desenho ou régua): os traços ficam inertes — o gesto é de
   * quem tem a ferramenta, não da seleção (F-E47.F3-03). Sem isto, um traço
   * iniciado em cima do alvo de clique da sugestão selecionava a sugestão em vez
   * de desenhar; o `AnnotationLayer` já se protege assim (`--inert`).
   */
  inert?: boolean;
  /** Dimensões naturais da contestada — testes injetam; produção mede do <img>. */
  naturalSize?: ImageSize | null;
}

/** Traço tracejado: distingue a sugestão da anotação do perito a olho nu. */
function dashFor(size: ImageSize): number {
  return annotationStrokePx(size) * 3;
}

/**
 * Cópia invisível e larga da geometria — alvo de clique generoso para traço fino
 * (mesma tática do `HitSvg` do AnnotationLayer, E36.F3). Sem ela, o alvo real é
 * o traço de poucos px: o clique cai no `fill="none"` e a sugestão não seleciona
 * (o smoke E2E pegou exatamente isto).
 */
function HitShape({
  annotation,
  size,
}: {
  annotation: MesaAnnotationData;
  size: ImageSize;
}): JSX.Element {
  const hit = Math.max(14, Math.round(Math.max(size.width, size.height) * 0.02));
  const geometry = annotation.geometry;
  if (isArrowGeometry(geometry)) {
    return (
      <line
        x1={geometry.x1 * size.width}
        y1={geometry.y1 * size.height}
        x2={geometry.x2 * size.width}
        y2={geometry.y2 * size.height}
        stroke="transparent"
        strokeWidth={hit}
      />
    );
  }
  return (
    <circle
      cx={geometry.cx * size.width}
      cy={geometry.cy * size.height}
      r={geometry.r * size.width}
      fill="none"
      stroke="transparent"
      strokeWidth={hit}
    />
  );
}

export function TriagemAnnotationLayer({
  annotations,
  selectedId,
  onSelect,
  inert = false,
  naturalSize = null,
}: TriagemAnnotationLayerProps): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<ImageSize | null>(null);
  const size = naturalSize ?? measured;

  // Mede as dimensões naturais do <img> irmão — mesma tática do AnnotationLayer.
  useEffect(() => {
    if (naturalSize !== null) return undefined;
    const img = wrapperRef.current?.parentElement?.querySelector('img') ?? null;
    if (img === null) return undefined;
    function measure(): void {
      if (img !== null && img.naturalWidth > 0) {
        setMeasured({ width: img.naturalWidth, height: img.naturalHeight });
      }
    }
    measure();
    img.addEventListener('load', measure);
    return () => img.removeEventListener('load', measure);
  }, [naturalSize]);

  return (
    <div
      ref={wrapperRef}
      className="annotation-layer capture-exclude"
      data-testid="triagem-annotation-layer"
    >
      {size !== null && annotations.length > 0 && (
        <svg
          className="annotation-surface"
          viewBox={`0 0 ${size.width} ${size.height}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={ptBR.workspaceCaso.triagemLayerLabel}
        >
          {annotations.map((annotation) => {
            const color = ANNOTATION_COLOR_HEX[annotation.color];
            const stroke = annotationStrokePx(size);
            const dash = dashFor(size);
            const geometry = annotation.geometry;
            const selected = annotation.id === selectedId;
            const common = {
              stroke: color,
              strokeWidth: selected ? stroke * 1.6 : stroke,
              strokeDasharray: `${dash} ${dash}`,
              fill: 'none' as const,
            };
            return (
              <g
                key={annotation.id}
                className={inert ? 'annotation-shape annotation-shape--inert' : 'annotation-shape'}
                data-testid={`triagem-shape-${annotation.id}`}
                data-tool={annotation.tool}
                data-color={annotation.color}
                data-selected={selected || undefined}
                data-inert={inert || undefined}
                onMouseDown={(event) => {
                  // `--inert` já corta o evento no browser (pointer-events:none),
                  // mas o jsdom não aplica pointer-events: sem este guard o
                  // teste de componente passaria por engano.
                  if (inert) return;
                  event.stopPropagation();
                  onSelect(annotation.id);
                }}
              >
                <HitShape annotation={annotation} size={size} />
                {isArrowGeometry(geometry) ? (
                  <>
                    <line
                      x1={geometry.x1 * size.width}
                      y1={geometry.y1 * size.height}
                      x2={geometry.x2 * size.width}
                      y2={geometry.y2 * size.height}
                      {...common}
                    />
                    <polygon
                      points={arrowHeadPoints(geometry, size, arrowHeadLenPx(size))}
                      fill={color}
                    />
                  </>
                ) : (
                  <circle
                    cx={geometry.cx * size.width}
                    cy={geometry.cy * size.height}
                    r={geometry.r * size.width}
                    {...common}
                  />
                )}
                {annotation.nota !== null && <title>{annotation.nota}</title>}
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}
